import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import debug from "debug";
import Experience from "../Experience.js";
import { assetURL } from "./config.js"; // Import assetURL from config.js
import { geomConfig } from "../config/geomConfig.js"; // Import Asset configuration
import AssetVault from "../Utils/AssetVault/AssetVault.js";
import AssetProcessor from "../Utils/AssetVault/AssetProcessor.js";
import { param } from "jquery";

const log = debug("app:asset");

/**
 * Class representing a shape generator in a 3D scene.
 * It manages the creation of 2D and 3D assets, texture application, and model loading.
 * @category Experience
 * @subcategory World
 */
export default class MakeShape {
  /**
   * Constructs an instance of `MakeShape`, initializing dependencies such as
   * texture vault, GLTF loader, and scene experience.
   */
  constructor() {
    this.experience = new Experience();
    this.textureVault = this.experience.textureVault; // Initialize TextureVault
    this.loader = this.experience.loader; // GLTFLoader for loading 3D models
    this.AssetVault = AssetVault.getInstance(this.experience); // Use AssetVault instance
    this.AssetProcessor = new AssetProcessor(this.experience); // Initialize AssetProcessor
  }

  cleanup() {
    try {
      log("Cleaning up MakeShape...");

      // Dispose of textures stored in TextureVault
      if (this.textureVault) {
        this.textureVault.clear(); // Assuming there's a method to clear cached textures
      }

      // Dispose of GLTFLoader
      if (this.loader) {
        this.loader = null; // GLTFLoader doesn't have explicit dispose, but we remove reference
      }

      // Dispose of AssetVault and AssetProcessor
      if (this.AssetVault) {
        this.AssetVault.clear(); // Assuming it has a clear method
      }

      if (this.AssetProcessor) {
        this.AssetProcessor = null;
      }

      // Dispose of materials and geometries
      this._disposeSceneObjects();

      // Clear references
      this.experience = null;
      this.textureVault = null;
      this.AssetVault = null;
      this.AssetProcessor = null;

      log("MakeShape cleanup completed.");
    } catch (error) {
      console.error("Error during MakeShape cleanup:", error);
    }
  }

  /**
   * Disposes of all materials, textures, and geometries in the scene.
   */
  _disposeSceneObjects() {
    try {
      this.experience.scene.traverse((object) => {
        if (object.isMesh) {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => {
                if (material.map) material.map.dispose();
                material.dispose();
              });
            } else {
              if (object.material.map) object.material.map.dispose();
              object.material.dispose();
            }
          }
        }
      });

      log("Disposed all scene objects.");
    } catch (error) {
      console.error("Error disposing scene objects:", error);
    }
  }

  /**
   * Initializes the shape creation process by generating geometry and applying textures.
   * @param {object} params - Parameters for shape creation.
   * @returns {Promise<THREE.Object3D>} - The created mesh or model group.
   */
  async init(params) {
    try {
      const { viewMode, glb } = params;

      if (!viewMode["3d"]) {
        const { asset, isAssetAvailable } = this._createAsset2D(params);
        return this.applyTexture(asset, params);
      }

      if (glb) {
        const { asset, isAssetAvailable } = await this._loadAsset3D(params);
        if (!isAssetAvailable) {
          const asset3D = this._createAsset3D(params);
          return asset3D;
        }
        return asset;
      }

      const { asset } = await this._createAsset3D(params);
      log("asset", asset);
      return asset;
    } catch (error) {
      console.error("Error in MakeShape init:", error);
      throw error;
    }
  }

  /**
   * Loads a 3D asset (GLB model) from the asset vault.
   * @param {object} params - The parameters containing the GLB path.
   * @returns {Promise<{asset: THREE.Object3D, isAssetAvailable: boolean}>} - The loaded asset.
   */
  async _loadAsset3D(params) {
    const { glb } = params;

    try {
      const asset = await this.AssetVault.getAsset(glb);
      log("asset in MakeShape", asset);
      const processedAsset = this.AssetProcessor.init(asset, params);
      processedAsset.name = "Shape";
      return { asset: processedAsset, isAssetAvailable: true };
    } catch (error) {
      console.error("Error loading GLB asset:", error);
      return { asset: null, isAssetAvailable: false };
    }
  }

  /**
   * Creates a 2D shape using a plane geometry.
   * @param {object} params - Parameters for the shape.
   * @param {number|null} y - Optional Y-coordinate.
   * @returns {{ asset: THREE.Mesh, isAssetAvailable: boolean }} - The created 2D plane.
   */
  _createAsset2D(params, y = null) {
    const { w, h, layer } = params;
    try {
      const geometry = new THREE.PlaneGeometry(w, h);
      geometry.attributes.uv.normalized = true;

      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 1,
        side: THREE.DoubleSide, // Ensuring both faces are visible
      });

      const plane = new THREE.Mesh(geometry, material);
      plane.name = "Shape";

      // Consistent layer-based rotation handling
      plane.rotation.y = layer === 10 ? -Math.PI : 0;
      plane.rotation.x = -Math.PI / 2;

      return { asset: plane, isAssetAvailable: true };
    } catch (error) {
      console.error("Error creating 2D asset:", error);
      return { asset: null, isAssetAvailable: false };
    }
  }

  /**
   * Creates a 3D shape with extrusion and applies necessary textures.
   * @param {object} params - Parameters for the 3D shape.
   * @returns {Promise<{ asset: THREE.Group, isAssetAvailable: boolean }>} - The created 3D shape.
   */
  async _createAsset3D(params) {
    const { layer, w, h } = params;
    try {
      // Find the layer configuration
      const layerConfig = geomConfig.layers.find(
        (config) => config.layer === layer
      );
      const layerBasePlate = geomConfig.layers.find(
        (config) => config.layer === 20
      );
      const basePlateThickness = layerBasePlate.y3;

      if (!layerConfig) {
        throw new Error(`Layer configuration not found for layer ${layer}`);
      }
      const thickness = layerConfig.t3 || 0.1;
      const cornerRadius = 0.4858; // Radius for the fillets

      // Create a shape with rounded corners in the X-Y plane
      const shape = new THREE.Shape();
      shape.moveTo(-w / 2 + cornerRadius, -h / 2); // Bottom-left corner
      shape.lineTo(w / 2 - cornerRadius, -h / 2); // Bottom edge
      shape.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + cornerRadius); // Bottom-right corner
      shape.lineTo(w / 2, h / 2 - cornerRadius); // Right edge
      shape.quadraticCurveTo(w / 2, h / 2, w / 2 - cornerRadius, h / 2); // Top-right corner
      shape.lineTo(-w / 2 + cornerRadius, h / 2); // Top edge
      shape.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - cornerRadius); // Top-left corner
      shape.lineTo(-w / 2, -h / 2 + cornerRadius); // Left edge
      shape.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + cornerRadius, -h / 2); // Back to start

      // Extrude the shape along the Z-axis to match the thickness
      const extrudeSettings = {
        depth: thickness, // Extrude depth matches the original thickness
        bevelEnabled: false, // No bevels, only fillets on the edges
      };
      let geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
      log("geometry before", geometry);
      geometry = this._splitAsset3DGroups(geometry);

      const topMaterial = new THREE.MeshBasicMaterial({
        color: 0xd1c184,
        transparent: true,
        opacity: 0,
      });

      const sideMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff, // Grey for sides
        side: THREE.SingleSide,
        metalness: 0.2,
        roughness: 0.8,
      });

      const bottomMaterial = new THREE.MeshStandardMaterial({
        color: 0xf1be70, // Gold for the bottom
        side: THREE.SingleSide,
        metalness: 0.2,
        roughness: 0.4,
      });

      const materials = [
        sideMaterial, // Left face
        topMaterial, // Top face
        bottomMaterial, // Bottom face
      ];
      log("geometry after", geometry);

      const box = new THREE.Mesh(geometry, materials);
      box.rotation.x = -Math.PI / 2;
      box.position.y = layer === 10 ? -thickness : 0;
      const plane = this._createAsset2D(params).asset;
      log("plane", plane);
      const planeTop = await this.applyTexture(plane, params);
      log("planeTop", planeTop);
      planeTop.position.y = layer === 10 ? -thickness : thickness; // Align perfectly with the top face
      planeTop.material.polygonOffset = true;
      planeTop.material.polygonOffsetFactor = -0.5; // Ensure no Z-fighting
      planeTop.material.polygonOffsetUnits = -1;
      planeTop.material.transparent = true;
      planeTop.material.opacity = 0.99; // Blend with top face
      planeTop.rotation.x = -Math.PI / 2; // Align with X-Y plane
      planeTop.rotation.y = layer === 10 ? -Math.PI : 0;
      const group = new THREE.Group();
      group.name = "Shape";

      group.add(box);
      group.add(planeTop);
      return { asset: group, isAssetAvailable: true };
    } catch (error) {
      console.error("Error creating 3D asset:", error);
      return { asset: null, isAssetAvailable: false };
    }
  }

  /**
   * Applies textures to the given mesh based on view mode and part configuration.
   * @param {THREE.Object3D} mesh - The mesh to apply textures to.
   * @param {object} params - Parameters including textures and view mode.
   * @param {object} params.textures - Texture configurations.
   * @param {object} params.viewMode - View mode settings.
   * @param {string} params.mode - The mode of the part (e.g., "cfg-part" or "xm-part").
   * @param {string} params.glb - Path to the GLB model file (if applicable).
   * @returns {Promise<THREE.Object3D>} - The textured mesh.
   */
  async applyTexture(mesh, params) {
    const { textures, viewMode, mode, glb } = params;
    let textureKey;

    if (viewMode["3d"] === true && glb) {
      // If in 3D mode and GLB is present, no texture application is needed here
      return mesh;
    } else {
      if (mode === "cfg-part") {
        textureKey = textures.cfg || "fallback";
      } else {
        viewMode.viewMode === "hybrid"
          ? (textureKey = textures.hyb || textures.brd || "fallback")
          : (textureKey = textures.brd || textures.hyb || "fallback");
      }
    }

    log("textureKey", textureKey);
    const texture = await this.textureVault.getTextures([textureKey]);
    log("texture", texture);

    if (mesh.material) {
      mesh.material.map = texture[0];
      mesh.material.needsUpdate = true;
    }

    return mesh;
  }

  _splitAsset3DGroups(geometry) {
    const positionArray = geometry.attributes.position.array;

    // Find the largest Z (top face) and smallest Z (bottom face)
    const zValues = [];
    for (let i = 2; i < positionArray.length; i += 3) {
      zValues.push(positionArray[i]); // Collect all Z values
    }
    const topZ = Math.max(...zValues);
    const bottomZ = Math.min(...zValues);

    // Prepare a new groups array
    const newGroups = [];

    // Iterate through all faces (non-indexed geometry, 3 vertices per face)
    for (let i = 0; i < positionArray.length; i += 9) {
      const z1 = positionArray[i + 2];
      const z2 = positionArray[i + 5];
      const z3 = positionArray[i + 8];

      if (z1 === topZ && z2 === topZ && z3 === topZ) {
        // Add to Group 1 (Top Face)
        newGroups.push({ start: i / 3, count: 3, materialIndex: 1 });
      } else if (z1 === bottomZ && z2 === bottomZ && z3 === bottomZ) {
        // Add to Group 2 (Bottom Face)
        newGroups.push({ start: i / 3, count: 3, materialIndex: 2 });
      }
    }

    // Keep Group 0 (sides) intact
    newGroups.unshift({
      start: 0,
      count: positionArray.length / 3,
      materialIndex: 0,
    });

    // Replace the geometry's groups with the new ones
    geometry.groups = newGroups;

    return geometry;
  }
}
